import React, { useState, useEffect } from "react";
import { GatsbyImage as Image, getImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import AddToCart from "../products/AddToCart";
import ProductVariant from "./ProductVariant";
import DisplayPrice from "./DisplayPrice";
import useProductImageVariant from "../../hooks/useProductImageVariant";
import ProductReviewStars from "./ProductReviewStars";
import { truncateShopifyId } from "../../utilities/truncateShopifyId";
import { useDataLayer } from "../../hooks/dataLayer";

export default function ProductCard({ product, position }) {
  const [variant, setVariant] = useState(product.variants[0]);
  const [variantImage, setVariantImage] = useState(null);
  const [imagetoggle, setImageToggle] = useState(true);
  const [showPriceFrom, setShowPriceFrom] = useState(true);
  const { getVariantImage } = useProductImageVariant(null);
  const originalDisplayImage = getImage(product.images[0]);
  const isLIVBundle =
    product.shopifyId.split("/").pop() === process.env.GATSBY_LIV_ID;

  useEffect(async () => {
    if (!variant || !product) {
      return;
    }
    if (imagetoggle) {
      const newVariantImage = await getVariantImage({
        product,
        variantId: variant.storefrontId,
      });
      setVariantImage(getImage(newVariantImage));
      setImageToggle(false);
    }
  }, [variant, getVariantImage, product]);

  const layerData = {
    event: "productClick",
    ecommerce: {
      currencyCode: product.priceRangeV2.minVariantPrice.currencyCode,
      click: {
        actionField: {
          list: "Collection Page",
        },
        products: {
          name: product.title,
          id: variant.shopifyId ? truncateShopifyId(variant.shopifyId) : "",
          price: variant.price,
          brand: "Thermacell",
          position: position,
          category: "",
          list: "Collection Page",
          handle: product.handle,
        },
      },
    },
  };

  const ProductClickTrigger = () => {
    if (window && window.dataLayer) {
      window.dataLayer.push(layerData);
    }
  };

  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });
  const sorted = product.variants.sort((a, b) => {
    return collator.compare(a.title, b.title);
  });

  return (
    <div className="product-card bg-white p-2 md:p-4 border text-left hds-transition transform relative hover:shadow-xl hover:border-gray-600 stagger-item stagger-up rounded z-10 is-showing flex flex-col gap-6">
      <Link
        className="rounded-md overflow-hidden"
        to={`/products/${product.handle}`}
        onClick={ProductClickTrigger}
      >
        <div className="flex flex-row justify-center">
          {variantImage ? (
            <Image
              className="product-card__image"
              image={variantImage}
              alt={product.title}
              objectFit="cover"
              placeholder="blurred"
              layout="constrained"
              width={300}
            />
          ) : (
            <Image
              image={originalDisplayImage}
              alt={product.title}
              className="product-card__image"
              objectFit="cover"
              placeholder="blurred"
              layout="constrained"
              width={300}
            />
          )}
        </div>
      </Link>
      <div className="flex flex-col justify-between flex-1">
        <Link
          className="flex-1 flex flex-col justify-start"
          to={`/products/${product.handle}`}
          onClick={ProductClickTrigger}
        >
          <h4 className="font-heading uppercase font-bold pb-1 leading-tight text-xl md:text-2xl text-center">
            {product.title}
          </h4>
        </Link>
        <div>
          {sorted.length > 1 && (
            <div className="flex row flex-wrap">
              {sorted.map((productVariant) => (
                <>
                  {productVariant.title.toLowerCase() !== "default" && (
                    <ProductVariant
                      key={productVariant.storefrontId}
                      productVariant={productVariant}
                      variantType={product.options[0].name}
                      variant={variant}
                      setImageToggle={setImageToggle}
                      setShowPriceFrom={setShowPriceFrom}
                      setVariant={setVariant}
                    />
                  )}
                </>
              ))}
            </div>
          )}
          <div className="pt-2">
            <ProductReviewStars
              link={`/products/${product.handle}#reviews`}
              product={product}
            />
          </div>
          <DisplayPrice
            product={product}
            variant={variant}
            showPriceFrom={showPriceFrom}
          />
          <div className="flex flex-col mx-auto w-full md:w-1/2">
            {product.title == "Radius Zone Mosquito Repellent - Gen 2.0" ? (
              <button
                className="add-to-cart fg-btn fg-btn--blue false w-full fg-btn__disabled"
                type="button"
                disabled="true"
              >
                Not Available
              </button>
            ) : (
              ""
            )}
            {!isLIVBundle &&
              product.title !== "Radius Zone Mosquito Repellent - Gen 2.0" && (
                <AddToCart
                  variant={variant}
                  product={variant.product}
                  quantity={1}
                  classNames={`add-to-cart fg-btn fg-btn--blue ${
                    !variant.availableForSale && "fg-btn__disabled"
                  }`}
                  disabled={!variant.availableForSale}
                />
              )}

            {isLIVBundle && (
              <Link to={`/products/${product.handle}`}>
                <a className="block fg-btn">View</a>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
